/**
 * Shared constants, formatters, and components used across all performance charts.
 */
import React, { useState, useRef, useLayoutEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';

// ── Color palettes ──────────────────────────────────────────────────────────

export const REGION_COLORS = {
  NSW1: '#3b82f6',
  VIC1: '#8b5cf6',
  QLD1: '#f59e0b',
  SA1: '#ef4444',
  TAS1: '#06b6d4',
};

export const REGION_LABELS = {
  NSW1: 'NSW',
  VIC1: 'VIC',
  QLD1: 'QLD',
  SA1: 'SA',
  TAS1: 'TAS',
};

export const FUEL_COLORS = {
  Solar: '#f59e0b',
  Wind: '#3b82f6',
  Gas: '#ef4444',
  Hydro: '#06b6d4',
  'Black Coal': '#374151',
  'Brown Coal': '#78350f',
  Battery: '#8b5cf6',
  Other: '#6b7280',
};

export const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export const YEAR_COLORS = [
  '#D98A2B', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1', '#14b8a6',
];

/**
 * General-purpose chart palette — use this any time you need a sequence
 * of distinct colours and don't have a domain-specific palette
 * (FUEL_COLORS, REGION_COLORS). Series N gets `CHART_PALETTE[N % len]`.
 *
 * Same colours as YEAR_COLORS, exposed under a name that doesn't imply
 * a year axis. AgentChart + any spec-driven chart should read from here.
 */
export const CHART_PALETTE = YEAR_COLORS;

export const generateAssetColor = (index) => {
  const hue = (index * 137.508) % 360;
  const saturation = 65 + (index % 3) * 8;
  const lightness = 48 + (index % 2) * 8;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// ── Date / value formatters ─────────────────────────────────────────────────

export const formatDateTime = (raw, frequency) => {
  const s = String(raw);
  if (frequency === '30MIN') {
    if (s.length >= 12) return `${s.slice(4, 6)}/${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
    if (s.length >= 10) return `${s.slice(4, 6)}/${s.slice(6, 8)} ${s.slice(8, 10)}:00`;
  }
  if (frequency === 'DAILY' && s.length >= 8) return `${s.slice(4, 6)}/${s.slice(6, 8)}`;
  if (frequency === 'MONTHLY' && s.length >= 6) {
    const mi = parseInt(s.slice(4, 6), 10);
    return `${MONTH_NAMES[mi - 1]} ${s.slice(0, 4)}`;
  }
  if (frequency === 'YEARLY') return s.slice(0, 4);
  return s;
};

export const formatMonth = (raw) => {
  const s = String(raw);
  if (s.length >= 6) {
    const mi = parseInt(s.slice(4, 6), 10);
    return `${MONTH_NAMES[mi - 1]} '${s.slice(2, 4)}`;
  }
  return s;
};

/** Format a period string based on granularity: YYYYMMDD → "Dec 01", YYYYMM → "Dec '25". */
export const formatPeriod = (raw, granularity) => {
  const s = String(raw);
  if (granularity === 'daily' && s.length >= 8) {
    const mi = parseInt(s.slice(4, 6), 10);
    const day = s.slice(6, 8);
    return `${MONTH_NAMES[mi - 1]} ${day}`;
  }
  // monthly fallback
  if (s.length >= 6) {
    const mi = parseInt(s.slice(4, 6), 10);
    return `${MONTH_NAMES[mi - 1]} '${s.slice(2, 4)}`;
  }
  return s;
};

export const formatMWh = (v) => {
  if (v === 0) return '0';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} TWh`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(abs >= 10_000 ? 0 : 1)} GWh`;
  if (abs < 1) return `${(v * 1_000).toFixed(0)} kWh`;
  return `${v.toFixed(v >= 100 ? 0 : 1)} MWh`;
};

export const formatAUD = (v) => {
  if (v === 0) return 'A$0';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `A$${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `A$${(v / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  return `A$${v.toFixed(0)}`;
};

export const formatPrice = (v) => {
  if (v === 0) return 'A$0';
  return `A$${v.toFixed(v >= 100 ? 0 : 2)}`;
};

export const formatMW = (v) => {
  if (v === 0) return '0';
  return `${v.toFixed(v >= 100 ? 0 : 1)} MW`;
};


/**
 * Name → y-axis formatter mapping. Spec-driven charts (AgentChart, etc.)
 * use this so a single `y_axis.format` string in the spec controls how
 * the axis ticks + tooltips render. Add a key here to expose a new
 * format to spec-driven charts — bespoke charts can import the
 * formatter functions directly above.
 */
export const Y_FORMATTERS = {
  aud:           (v) => `A$${Math.round(v).toLocaleString('en-AU')}`,
  aud_compact:   (v) => formatAUD(v),
  aud_per_mwh:   (v) => formatPrice(v),
  mwh:           (v) => formatMWh(v),
  mw:            (v) => formatMW(v),
  percent:       (v) => `${Number(v).toFixed(1)}%`,
  number:        (v) => Number(v).toLocaleString('en-AU'),
};

// ── Responsive width hook ───────────────────────────────────────────────────

export const useContainerWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const update = () => {
      if (ref.current) {
        const w = ref.current.offsetWidth;
        if (w > 0) setWidth(w);
      }
    };
    const tid = setTimeout(update, 0);
    const ro = new ResizeObserver(() => requestAnimationFrame(update));
    if (ref.current) ro.observe(ref.current);
    window.addEventListener('resize', update);
    return () => {
      clearTimeout(tid);
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  return { ref, width };
};

// ── Shared UI components ────────────────────────────────────────────────────

export const ChartSection = ({ title, subtitle, headerRight, children }) => (
  <Paper
    sx={{
      p: { xs: 2, md: 3 },
      mb: 3,
      border: '1px solid rgba(0,0,0,0.06)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}
  >
    <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary', letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.3 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {headerRight && <Box sx={{ flexShrink: 0 }}>{headerRight}</Box>}
    </Box>
    {children}
  </Paper>
);

export const EmptyState = ({ message }) => (
  <Box sx={{ py: 6, textAlign: 'center' }}>
    <Typography color="text.secondary">{message}</Typography>
  </Box>
);

export const StatCard = ({ label, value, unit, color }) => (
  <Paper
    sx={{
      px: 2.5,
      py: 2,
      flex: 1,
      minWidth: 160,
      border: '1px solid rgba(0,0,0,0.06)',
      boxShadow: 'none',
    }}
  >
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: color || 'text.primary', letterSpacing: '-0.02em' }}>
        {value}
      </Typography>
      {unit && (
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'text.secondary' }}>
          {unit}
        </Typography>
      )}
    </Box>
  </Paper>
);

/** Standard MUI X-Charts axis + legend config for consistency. */
export const CHART_MARGINS = { top: 20, right: 20, left: 80, bottom: 100 };
export const CHART_MARGINS_SIMPLE = { top: 20, right: 20, left: 70, bottom: 60 };

export const defaultLegendProps = {
  direction: 'row',
  position: { vertical: 'bottom', horizontal: 'middle' },
  labelStyle: { fill: '#5a6a7e', fontSize: 11 },
  itemMarkWidth: 10,
  itemMarkHeight: 10,
  markGap: 4,
  itemGap: 12,
};

export const defaultLineLegendProps = {
  ...defaultLegendProps,
  labelStyle: { fill: '#5a6a7e', fontSize: 12 },
  itemMarkWidth: 14,
  itemMarkHeight: 3,
  markGap: 5,
  itemGap: 16,
};

export const tickLabelStyle = { fontSize: 11, fill: '#5a6a7e' };
export const rotatedTickLabelStyle = { angle: -45, textAnchor: 'end', fontSize: 11, fill: '#5a6a7e' };

/** Standard toggle button group styling. */
export const toggleButtonGroupSx = {
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.78rem',
    px: 1.5,
    py: 0.4,
    borderColor: 'rgba(0,0,0,0.12)',
    '&.Mui-selected': {
      bgcolor: 'primary.main',
      color: '#fff',
      '&:hover': { bgcolor: 'primary.dark' },
    },
  },
};
